import { useState } from 'react';
import { TextField } from '@mui/material';
import IncFormHelperText from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import { Controller, useFormContext } from 'react-hook-form';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import useCheckAutoFill from 'hooks/use-check-auto-fill';

type PasswordFieldType = {
  name: string;
  label: string;
  helperText?: string;
  autoComplete: string;
};

const PasswordField = ({
  name,
  helperText,
  autoComplete,
  ...rest
}: PasswordFieldType) => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const [autoFilled, handleCheckAnimation] = useCheckAutoFill();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...fieldRest }, fieldState: { error } }) => (
        <TextField
          {...fieldRest}
          type={showPassword ? 'text' : 'password'}
          inputRef={ref}
          error={!!error}
          helperText={
            <IncFormHelperText error={error} helperText={helperText} />
          }
          InputProps={{
            autoComplete,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  data-testid="PasswordField.IconButton"
                  aria-label={t('forms.fields.password.endAdornment.ariaLabel')}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onAnimationStart={handleCheckAnimation}
          InputLabelProps={{ shrink: autoFilled || !!fieldRest.value }}
          {...rest}
        />
      )}
    />
  );
};

export default PasswordField;
